import { ASSET_CATEGORY, ASSET_STATUSES, DISPATCH_TICKET_STATUSES, DISPATCH_TICKET_TYPES } from 'utils/constants'
import { combineDateAndTime } from 'components/containers/load-board/utils/helperFunctions'
import store from 'state/store'
import { convertShippingInfoToCityStateZip, formatAddress } from 'utils/data-manipulation/valueFormatter'
import { UpdateDispatchTicketBody } from 'gen/rtk-openapi'

const formatDispatchTicketAsset = (asset, defaultValue) => ({
  ...asset,
  assetId: asset.assetId || asset.id || defaultValue,
  status: asset.status || ASSET_STATUSES.IN_PROGRESS.id,
  note: asset.note || defaultValue,
  completionNote: asset.completionNote || defaultValue,
  dispatcherCompletionNote: asset.dispatcherCompletionNote || defaultValue,
  toLocationId: asset.toLocationId || defaultValue,
  fromLocationId: asset.fromLocationId || defaultValue,
  images: asset.images || [],
})

/**
 * map the data from the API to the format expected by the form
 */
export const formatGetDispatchTicket = data => {
  const storeState = store.getState()
  const { yardDetailsList } = storeState.auth

  const dispatchingYardLocationId = yardDetailsList?.find(yard => yard.id === data.yardId)?.locationId

  const dispatchTicketContainerAssets = data.dispatchTicketAssets?.filter(
    asset => asset?.asset?.assetCategory === ASSET_CATEGORY.CONTAINER,
  )
  const dispatchTicketTruckAssets = data.dispatchTicketAssets?.filter(
    asset => asset?.asset?.assetCategory === ASSET_CATEGORY.TRUCK,
  )
  const trailerAsset = data.dispatchTicketAssets?.find(asset => asset?.asset?.assetCategory === ASSET_CATEGORY.TRAILER)

  const ticketStatus = data.status
  let trailerGoingToCustLocation = false
  let pickUpAndDropOffDifferent = false
  let trailerPickAndReturn = false
  let trailerInterYard = false
  if (ticketStatus === DISPATCH_TICKET_STATUSES.FAILED.id) {
    const currTrailerLocation = trailerAsset?.toLocationId
    // means trailer is not at DT target destination
    trailerGoingToCustLocation = data.locationId !== currTrailerLocation
  } else {
    pickUpAndDropOffDifferent = trailerAsset?.fromLocationId !== trailerAsset?.toLocationId
    trailerInterYard = data?.interYard?.endLocationId === trailerAsset?.toLocationId
    trailerGoingToCustLocation = pickUpAndDropOffDifferent && data.locationId === trailerAsset?.toLocationId
    trailerPickAndReturn = !pickUpAndDropOffDifferent && data.locationId === trailerAsset?.toLocationId
  }

  const dispatchTicketAssets = dispatchTicketContainerAssets?.map(asset => formatDispatchTicketAsset(asset, ''))

  const { requestDate, scheduleDate, scheduleTimeActive } = data
  const requestDateIsInvalid = !requestDate || Number.isNaN(new Date(requestDate).getTime())
  const scheduleDateIsInvalid = !scheduleDate || Number.isNaN(new Date(scheduleDate).getTime())

  const now = new Date()
  const sanitizedRequestDate = requestDateIsInvalid ? now : new Date(requestDate)
  const sanitizedScheduleDate = scheduleDateIsInvalid ? now : new Date(scheduleDate)

  return {
    status: data.status || DISPATCH_TICKET_STATUSES.UNASSIGNED.id,
    type: data.type || DISPATCH_TICKET_TYPES.PICK_UP.id,
    externalId: data.externalId || '',
    customerId: data.customerId || data.customer?.id || '',
    contactId: data.contactId || data.contact?.id || '',
    driverId: data.driverId || data.driver?.id || '',
    locationId: data.locationId || data.location?.id || '',
    location: {
      id: data.locationId || data.location?.id || '',
      name: data.location?.name || '',
      address: data.location?.shippingAddress || data.location?.address1 || data.location?.address || '',
      city: data.location?.city || '',
      state: data.location?.state || '',
      zip: data.location?.zip || '',
      notes: data.location?.notes || '',
    },
    yardId: data.yardId,
    requestDate: sanitizedRequestDate,
    requestTime: sanitizedRequestDate,
    requestNotes: data.requestNotes,
    scheduleDate: sanitizedScheduleDate,
    scheduleTime: scheduleTimeActive ? sanitizedScheduleDate : null,
    startTime: data.startTime || '',
    completionTime: data.completionTime || '',
    note: data.note || '',
    docs: data.docs || [],
    recurrence: data.recurrence || '',
    mileage: data.mileage || '',
    assetTruckId: dispatchTicketTruckAssets?.[0]?.assetId || '',
    assetTrailerId: trailerAsset?.assetId || '',
    dispatchingYardLocationId: data.interYard ? data.interYard.startLocationId : dispatchingYardLocationId,
    terminatingYardLocationId: data.interYard ? data.interYard.endLocationId : dispatchingYardLocationId,
    dropOffTrailer: data.type === DISPATCH_TICKET_TYPES.DROP_OFF.id && trailerGoingToCustLocation,
    pickUpTrailer:
      data.type === DISPATCH_TICKET_TYPES.PICK_UP.id &&
      pickUpAndDropOffDifferent &&
      !trailerGoingToCustLocation &&
      !trailerInterYard,
    pickAndReturnTrailer: data.type === DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id && trailerPickAndReturn,
    dispatchTicketAssets,
    intercompany: data.intercompany || null,
  }
}

/**
 * maps the form data model to the format expected by the API
 */
export const formatSubmitData = data => {
  const storeState = store.getState()
  const { inOrgMode } = storeState.ui
  const { yardDetailsList, yardId } = storeState.auth

  const {
    dispatchingYardLocationId,
    terminatingYardLocationId,
    pickupAssets,
    dropoffAssets,
    pickAndReturnAssets,
    deliveryAssets,
    liveLoadAssets,
    assetTruckId,
    assetTrailerId,
    dropOffTrailer,
    pickUpTrailer,
    pickAndReturnTrailer,
  } = data
  let yard
  if (inOrgMode) {
    yard = yardDetailsList?.find(
      yard => yard.defaultLocationId === dispatchingYardLocationId || yard.locationId === dispatchingYardLocationId,
    )
  } else {
    yard = yardDetailsList?.find(yard => yard.id === yardId)
  }
  const customerLocationId = data.location.id
  const isInterYard =
    !!dispatchingYardLocationId &&
    !!terminatingYardLocationId &&
    dispatchingYardLocationId !== terminatingYardLocationId

  // when inter yard the endYardLocationId will be different from the dispatchingYardLocationId
  const endYardLocationId = isInterYard ? terminatingYardLocationId : dispatchingYardLocationId

  // mapping that is the same across dispatch ticket types
  const mapAssetGeneric = asset => {
    return {
      assetId: asset.assetId || asset.id || null,
      status: asset.status || ASSET_STATUSES.IN_PROGRESS.id,
      note: asset.note || null,
      completionNote: asset.completionNote || null,
      dispatcherCompletionNote: asset.dispatcherCompletionNote || null,
    }
  }
  // set pickup assets as from customer to yard
  const mapPickupAssets = assets => {
    return assets.map(asset => ({
      ...mapAssetGeneric(asset),
      toLocationId: endYardLocationId,
      fromLocationId: customerLocationId,
    }))
  }
  // set dropoff assets as from yard to customer
  const mapDropoffAssets = assets => {
    return assets.map(asset => ({
      ...mapAssetGeneric(asset),
      toLocationId: customerLocationId,
      fromLocationId: dispatchingYardLocationId,
    }))
  }
  // set pick and return assets as from and to customer
  const mapPickAndReturnAssets = assets => {
    return assets.map(asset => ({
      ...mapAssetGeneric(asset),
      toLocationId: customerLocationId || null,
      fromLocationId: customerLocationId || null,
    }))
  }
  // set delivery assets as from yard and to yard
  const mapDeliveryAssets = assets => {
    return assets.map(asset => ({
      ...mapAssetGeneric(asset),
      toLocationId: endYardLocationId,
      fromLocationId: dispatchingYardLocationId,
    }))
  }

  // Reference the type of dispatchTicketAssets
  type DispatchTicketAssetsType = UpdateDispatchTicketBody['dispatchTicketAssets']

  let dispatchTicketAssets: DispatchTicketAssetsType = []
  const hasNewAssets = pickupAssets || dropoffAssets || pickAndReturnAssets || deliveryAssets || liveLoadAssets
  // runs formatting on asset statuses added by the user (exist in state but not persisted yet)
  if (hasNewAssets) {
    dispatchTicketAssets = [
      ...(pickupAssets?.length ? mapPickupAssets(pickupAssets) : []),
      ...(dropoffAssets?.length ? mapDropoffAssets(dropoffAssets) : []),
      ...(pickAndReturnAssets?.length ? mapPickAndReturnAssets(pickAndReturnAssets) : []),
      ...(deliveryAssets?.length ? mapDeliveryAssets(deliveryAssets) : []),
      ...(liveLoadAssets?.length ? mapDeliveryAssets(liveLoadAssets) : []), // same mapper as delivery assets
    ]
  } else {
    // runs formatting on asset statuses fetched from the API (already persisted)
    dispatchTicketAssets = data.dispatchTicketAssets?.map(asset => formatDispatchTicketAsset(asset, null))
  }
  if (assetTruckId) {
    dispatchTicketAssets?.push({
      assetId: assetTruckId,
      toLocationId: endYardLocationId,
      fromLocationId: dispatchingYardLocationId,
    })
  }
  if (assetTrailerId) {
    dispatchTicketAssets?.push({
      assetId: assetTrailerId,
      toLocationId: dropOffTrailer || pickAndReturnTrailer ? customerLocationId : endYardLocationId,
      fromLocationId: pickUpTrailer || pickAndReturnTrailer ? customerLocationId : dispatchingYardLocationId,
    })
  }

  const { requestDate, requestTime, scheduleDate, scheduleTime } = data
  const requestDateIsInvalid = !requestDate || Number.isNaN(new Date(requestDate).getTime())
  const requestTimeIsInvalid = !requestTime || Number.isNaN(new Date(requestTime).getTime())
  const scheduleDateIsInvalid = !scheduleDate || Number.isNaN(new Date(scheduleDate).getTime())
  const scheduleTimeIsInvalid = !scheduleTime || Number.isNaN(new Date(scheduleTime).getTime())
  const now = new Date()

  const interYardDetails = inOrgMode
    ? {
        startLocationId: dispatchingYardLocationId,
        endLocationId: terminatingYardLocationId,
      }
    : null // null interYardDetails is needed to clear the interYard properties when a ticket is modified to not be interYard

  return {
    status:
      data.status || (data.driverId ? DISPATCH_TICKET_STATUSES.ASSIGNED.id : DISPATCH_TICKET_STATUSES.UNASSIGNED.id),
    type: data.type || DISPATCH_TICKET_TYPES.PICK_UP.id,

    externalId: data.externalId || null,
    customerId: data.customer?.id || null,
    driverId: data.driverId || null,
    locationId: data.location?.id || null,
    yardId: yard.id || null,

    requestDate: combineDateAndTime(
      requestDateIsInvalid ? now : new Date(requestDate),
      requestTimeIsInvalid ? now : new Date(requestTime),
    ),
    scheduleDate: combineDateAndTime(
      scheduleDateIsInvalid ? now : new Date(scheduleDate),
      scheduleTimeIsInvalid ? now : new Date(scheduleTime),
    ),
    scheduleTimeActive: !scheduleTimeIsInvalid,
    startTime: data.startTime || null,
    completionTime: data.completionTime || null,
    contactId: data.contactId || null,
    note: data.note || null,
    docs: data.docs || [],
    recurrence: data.recurrence || null,
    mileage: isNaN(Number(data.mileage)) ? null : data.mileage,
    interYard: interYardDetails,
    idempotencyKey: data.idempotencyKey || null,
    ...{ dispatchTicketAssets },
    hash: data.hash,
    intercompany: data.intercompany || null,
  }
}

export const formatSubmitBladeData = data => {
  const {
    startTime,
    yardDepartureDate,
    customerArrivalDate,
    customerDepartureDate,
    yardArrivalDate,
    yardReturnDepartureDate,
    customerReturnDepartureDate,
    customerReturnArrivalDate,
    yardReturnArrivalDate,
    completionTime,
  } = data

  return {
    ...data,
    mileage: isNaN(Number(data.mileage)) ? null : data.mileage,
    dispatchTicketAssets:
      data.dispatchTicketAssets?.map(asset => ({
        ...formatDispatchTicketAsset(asset, null),
        completionNote: data.driverCompletionNotes,
        dispatcherCompletionNote: data.dispatcherCompletionNotes,
      })) || [],
    startTime: toISOStringIfDate(startTime),
    completionTime: toISOStringIfDate(completionTime),
    yardDepartureDate: toISOStringIfDate(yardDepartureDate),
    customerArrivalDate: toISOStringIfDate(customerArrivalDate),
    customerDepartureDate: toISOStringIfDate(customerDepartureDate),
    yardArrivalDate: toISOStringIfDate(yardArrivalDate),
    yardReturnDepartureDate: toISOStringIfDate(yardReturnDepartureDate),
    customerReturnDepartureDate: toISOStringIfDate(customerReturnDepartureDate),
    customerReturnArrivalDate: toISOStringIfDate(customerReturnArrivalDate),
    yardReturnArrivalDate: toISOStringIfDate(yardReturnArrivalDate),
    dispatcherCompletionNotes: data.dispatcherCompletionNotes,
    driverCompletionNotes: data.driverCompletionNotes,
  }
}

export const formatSubmitAttachments = data => {
  const { uploadcareGroupId, docs } = data
  return {
    ...data,
    startTime: data.startTime || null,
    completionTime: data.completionTime || null,
    mileage: isNaN(Number(data.mileage)) ? null : data.mileage,
    dispatchTicketAssets:
      data.dispatchTicketAssets?.map(asset => ({
        ...formatDispatchTicketAsset(asset, null),
      })) || [],
    ...(uploadcareGroupId && { uploadcareGroupId }),
    docs: docs || [],
  }
}

// This is typed as any right now to allow for a bug fix. But can be cleaned up by
// accepting assetResponseScheme and the type of the state-stored assets in assetSelectionBlade
// or by fixing the format of the state-stored objects prior to passing in to this func
export const formatAssetForGrid = (asset: any, selectedLocation?: any) => {
  let locationText = ''
  if (selectedLocation) {
    locationText =
      typeof selectedLocation === 'object'
        ? `${selectedLocation.name} - ${formatAddress(selectedLocation)}, ${convertShippingInfoToCityStateZip(selectedLocation)}`
        : selectedLocation
  } else {
    const { currentLocation, toLocation, status } = asset?.assetStatus || {}
    const activeLocation =
      [ASSET_STATUSES.DEPLOYED.id, ASSET_STATUSES.INVENTORY.id].indexOf(status as any) > -1
        ? currentLocation
        : toLocation
    locationText = activeLocation
      ? `${status === ASSET_STATUSES.IN_PROGRESS.id ? 'En route to ' : ''}${activeLocation?.customer?.displayName} - ${activeLocation?.name}`
      : 'Unknown'
  }

  return {
    id: asset.id,
    assetName: asset?.assetName || asset?.asset?.assetName || '',
    dispatchTicketId: asset?.dispatchTicketId || asset?.assetStatus?.dispatchTicketId,
    status: asset?.assetStatus?.status || asset?.status || 'UNKNOWN',
    statusName: ASSET_STATUSES[asset?.assetStatus?.status || asset?.status]?.name || 'Unknown',
    type: asset?.type || asset?.asset?.type || '',
    make: asset?.make || asset?.asset?.make || '',
    model: asset?.model || asset?.asset?.model || '',
    plateState: asset?.plateState || asset?.asset?.plateState || '',
    licensePlate: asset?.plateNumber || asset?.asset?.plateNumber || '',
    totalRoutes: asset?.totalTrips || asset?.asset?.totalTrips || 0,
    location: locationText,
    daysOnSite: asset?.daysOnSite,
    notes: asset?.notes || '', // This is "notes". The field called "note" (without the s) is different - that field is stored on asset status
  }
}

function toISOStringIfDate(value) {
  if (value instanceof Date) {
    return value.toISOString()
  } else if (value) {
    return new Date(value).toISOString()
  }
  return undefined
}
